import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CREATE_COLLECTION,
  UPDATE_COLLECTION,
  LINK_IMAGE_WITH_COLLECTION,
} from "../../../services/graphql/mutation/Collection";
import { Upload } from "antd";
import { storage } from "../../../firebase";
import {
  deleteObject,
  getDownloadURL,
  ref,
  StorageReference,
  uploadBytes,
} from "firebase/storage";
import { FaUpload } from "react-icons/fa";
import { useUnauthorizedAction } from "../../../services/hooks/useUnauthorizedAction";
import { MessageError } from "../../../shared/MessageError";
import { MessageSuccess } from "../../../shared/MessageSuccess";
import { ICollectionAdmin } from "../../../services/graphql/Collection";
import moment from "moment";

interface IFormCreateCollection {
  editMode?: boolean;
  collectionToUpdate?: ICollectionAdmin;
  onSuccess?: () => void;
  onError?: () => void;
}
export const FormCreateCollection = (props: IFormCreateCollection) => {
  const { onSuccess, onError, editMode, collectionToUpdate } = props;
  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [createCollection] = useMutation(CREATE_COLLECTION);
  const [updateCollection] = useMutation(UPDATE_COLLECTION);
  const [linkImageWithCollection] = useMutation(LINK_IMAGE_WITH_COLLECTION);
  const [file, setFile] = useState<any>();
  const { action } = useUnauthorizedAction();
  const toast = useToast();
  const unauthorized = useCallback(() => {
    action();
  }, []);
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm();
  async function onSubmitCreate(values: any) {
    setLoadingForm(true);
    let refImage = "";
    let urlImage = "";
    if (file) {
      refImage = `${moment(new Date()).format(
        "DD-MM-YYYY HH:mm:ss"
      )}-collection`;
      const storageRef = ref(storage, refImage);
      await uploadBytes(storageRef, file as Blob).catch((error) => {
        console.log(error);
      });
      await getDownloadURL(ref(storageRef))
        .then((url) => {
          urlImage = url;
        })
        .catch((error) => {
          console.log(error);
        });
    }

    await createCollection({
      variables: {
        input: { ...values },
      },
    })
      .then(async (dataCollection) => {
        const { data } = dataCollection;
        const { createCollection } = data;
        if (refImage && urlImage) {
          await linkImageWithCollection({
            variables: {
              input: {
                id: createCollection.id,
                url_image: urlImage,
                ref_image: refImage,
              },
            },
          }).catch((error) => {
            toast({
              duration: 2500,
              position: "top",
              render: () =>
                MessageError(
                  error.message
                    ? error.message
                    : "Ocurrió un error al cargar la imagen."
                ),
            });
            if (onError) onError();
          });
        }
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "Tu colección se registró exitosamente!.",
            }),
        });
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error) => {
        console.log(error.message);
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Ocurrió un error al registrar su colección. Por favor intente nuevamente."
              ),
          });
          if (onError) {
            onError();
          }
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoadingForm(false);
      });
  }

  async function onSubmitUpdate(values: any) {
    if (!collectionToUpdate || !collectionToUpdate?.id) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError(
            "No pudimos encontrar la colección para poder acutalizarlo."
          ),
      });
      if (onError) {
        onError();
      }
      return;
    }
    setLoadingForm(true);
    let refImage = "";
    let urlImage = "";
    let oldStorageRef: StorageReference;
    if (file) {
      if (collectionToUpdate.ref_image) {
        oldStorageRef = ref(storage, collectionToUpdate.ref_image);
      }
      refImage = `${moment(new Date()).format(
        "DD-MM-YYYY HH:mm:ss"
      )}-collection`;
      const storageRef = ref(storage, refImage);
      await uploadBytes(storageRef, file as Blob).catch((error) => {
        console.log(error);
      });
      await getDownloadURL(ref(storageRef))
        .then((url) => {
          urlImage = url;
        })
        .catch((error) => {
          console.log(error);
        });
    }
    if (values.catalog_number) {
      values.catalog_number = Number(values.catalog_number);
    } else {
      delete values.catalog_number;
    }
    if (values.price) {
      values.price = Number(values.price);
    } else {
      delete values.price;
    }
    if (values.stock) {
      values.stock = Number(values.stock);
    } else {
      delete values.stock;
    }
    if (values.collection_id) {
      values.collection_id = Number(values.collection_id);
    } else {
      delete values.collection_id;
    }

    setLoadingForm(true);
    await updateCollection({
      variables: {
        input: { id: collectionToUpdate.id, ...values },
      },
    })
      .then(async (dataCollection) => {
        const { data } = dataCollection;
        const { updateCollection } = data;
        if (refImage && urlImage) {
          await linkImageWithCollection({
            variables: {
              input: {
                id: updateCollection.id,
                url_image: urlImage,
                ref_image: refImage,
              },
            },
          })
            .then(async () => {
              if (oldStorageRef) {
                await deleteObject(oldStorageRef).catch((error) => {
                  console.log(error);
                });
              }
            })
            .catch((error) => {
              toast({
                duration: 2500,
                position: "top",
                render: () =>
                  MessageError(
                    error.message
                      ? error.message
                      : "Ocurrió un error al cargar la imagen."
                  ),
              });
              if (onError) onError();
            });
        }
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "Tu colección se actualizó exitosamente!.",
            }),
        });
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Ocurrió un error al actualizar su colección. Por favor intente nuevamente."
              ),
          });
          if (onError) {
            onError();
          }
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoadingForm(false);
      });
    if (onSuccess) {
      onSuccess();
    }
  }

  return (
    <>
      <Flex
        alignItems={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        mb={2}
      >
        <CloseButton
          ml={"auto"}
          display={{ base: "block", sm: "none" }}
          onClick={() => {
            if (onSuccess) {
              onSuccess();
            }
          }}
        />
        <Heading mb={0} as={"h4"}>
          {!editMode ? "Agregar colección" : "Actualizar colección"}
        </Heading>
        <CloseButton
          ml={"auto"}
          display={{ base: "none", sm: "block" }}
          onClick={() => {
            if (onSuccess) {
              onSuccess();
            }
          }}
        />
      </Flex>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(!editMode ? onSubmitCreate : onSubmitUpdate)}
      >
        <FormControl isInvalid={errors.name}>
          <FormLabel>Nombre de la colección</FormLabel>
          <Input
            defaultValue={collectionToUpdate?.name}
            maxLength={30}
            placeholder="Ingrese nombre..."
            id="name"
            {...register("name", {
              required: "Debe ingresar un nombre",
            })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.description}>
          <FormLabel>Descripción</FormLabel>
          <Input
            defaultValue={collectionToUpdate?.description}
            maxLength={30}
            placeholder="Ingrese descripción..."
            id="description"
            {...register("description", {
              required: "Debe ingresar una descripción",
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </FormControl>

        <Upload
          customRequest={async (uploadRequestOptions) => {
            const { onSuccess, onError, file } = uploadRequestOptions;
            setFile(file);
            if (onSuccess) onSuccess("asd");
          }}
        >
          <Button style={{ marginTop: "15px" }} leftIcon={<FaUpload />}>
            Click para cargar imagen
          </Button>
        </Upload>
        <Button isLoading={loadingForm} type="submit" w={"100%"} mt={4}>
          {!editMode ? "Agregar" : "Guardar"}
        </Button>
      </form>
    </>
  );
};
